// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, InputLabel, Stack, Typography } from '@mui/material';
import { Views, ViewsProps } from 'react-big-calendar';
import { useNavigate, useSearchParams } from 'react-router-dom';
// Components, Layouts, Pages
import { FormScheduleModal, HeaderCalendar } from '~/components/module/schedules';
import {
  BaseFilters,
  BaseSelect,
  BaseSplitButton,
  BaseToolbar,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import Calendar from '~/components/module/schedules/calendar/Calendar';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { IFilterSchedule, IParamSchedule, ISchedule } from '~/utils/interface/schedule';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { CalendarViewMode } from '~/utils/type/common';
import { getScheduleList } from '~/thunks/schedule/scheduleThunk';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  DEFAULT_VALUE_FILTER,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { mockScheduleFilterData } from '~/utils/mockData/schedule';
import { IBaseOption, IQueryParamBase } from '~/utils/interface/common';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import { getFullName } from '~/utils/helper/common';
import { EmployeeStatusEnum, PageTypeEnum, RoleEnum, ScheduleTypeEnum } from '~/utils/enum/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
// Styles, images, icons
import styles from './Schedule.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const Schedule = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule']);
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const { currentTime, scheduleMode, calendarViewMode, events, timeLineEvents, needRefreshData } =
    useAppSelector((state) => state.schedule);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowFormScheduleModal, setIsShowFormScheduleModal] = useState<boolean>(false);
  const [scheduleId, setScheduleId] = useState<string>(EMPTY_STRING);

  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<IBaseOption[]>([]);
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [searchKeyEmployee, setSearchKeyEmployee] = useState<string>(EMPTY_STRING);
  const debounceEmployeeSearchKey = useDebounce(searchKeyEmployee, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [valueFilter, setValueFilter] = useState<IFilterSchedule>({
    businessTypes: [params?.businessTypes || DEFAULT_VALUE_FILTER],
    employeeIds: employeeIds || [],
  });

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const timeValue = useMemo(() => {
    switch (calendarViewMode) {
      case Views.WEEK:
        const startOfWeek = moment(currentTime).startOf('isoWeek').format('MMMM DD');
        const endOfWeek = moment(currentTime).endOf('isoWeek').format('MMMM DD');
        return `${startOfWeek} - ${endOfWeek}`;
      case Views.DAY:
        return moment(currentTime).format('dddd, MMMM DD');
      default:
        return moment(currentTime).format('MMMM YYYY');
    }
  }, [calendarViewMode, currentTime]);

  const memoEvents = useMemo(() => {
    return events;
  }, [events]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const startDate = moment(currentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const endDate = moment(currentTime).endOf(calendarViewMode).format('YYYY-MM-DD');
    const view = calendarViewMode.toUpperCase();
    const { businessTypes } = params;

    const queryParam: IParamSchedule = {
      ...(calendarViewMode !== 'month'
        ? { page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: DEFAULT_LIMIT_1000 }
        : {}),
      startDate,
      endDate,
      view,
      businessTypes: businessTypes
        ? [businessTypes]
        : [ScheduleTypeEnum.BOOKING_A_JOB, ScheduleTypeEnum.ESTIMATE, ScheduleTypeEnum.EVENT],
      employeeIds,
    };

    handleGetScheduleList(queryParam);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarViewMode, currentTime, params, valueFilter]);

  useEffect(() => {
    if (!needRefreshData) return;

    const startDate = moment(currentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const endDate = moment(currentTime).endOf(calendarViewMode).format('YYYY-MM-DD');
    const view = calendarViewMode.toUpperCase();
    const { businessTypes } = params;

    const queryParam: IParamSchedule = {
      ...(calendarViewMode !== 'month'
        ? { page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: DEFAULT_LIMIT_1000 }
        : {}),
      startDate,
      endDate,
      view,
      businessTypes: businessTypes
        ? [businessTypes]
        : [ScheduleTypeEnum.BOOKING_A_JOB, ScheduleTypeEnum.ESTIMATE, ScheduleTypeEnum.EVENT],
      employeeIds,
    };

    handleGetScheduleList(queryParam);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefreshData]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };

    handleGetListEmployee(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceEmployeeSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListEmployee = (params: IQueryParamBase) => {
    setIsLoadingEmployee(true);

    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        if (!res || !res?.data) return;

        const { responses } = res?.data;

        if (!responses) return;

        const employeeFiltered = responses?.filter(
          (employee) => employee?.status === EmployeeStatusEnum.ACTIVE
        );

        const employeeOptions = employeeFiltered?.map((employee) => ({
          label: getFullName({ ...employee }),
          value: employee?.id,
        }));

        setEmployeeList(employeeOptions || []);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingEmployee(false);
      });
  };

  const handleGetScheduleList = (queryParam: IParamSchedule) => {
    dispatch(scheduleActions.changeEvents([]));
    dispatch(scheduleActions.changeTimeLineEvents([]));

    setIsLoading(true);

    dispatch(getScheduleList(queryParam))
      .unwrap()
      .then((res) => {})
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onSelectEvent = (event: ISchedule) => {
    setScheduleId(event.id);
  };

  const handleShowFormSchedule = () => {
    setIsShowFormScheduleModal(true);
  };

  const handleCloseFormScheduleModal = () => {
    setIsShowFormScheduleModal(false);
    setScheduleId(EMPTY_STRING);
  };

  const handleChangeCalendarView = (viewMode: CalendarViewMode) => {
    dispatch(scheduleActions.changeCalendarViewMode(viewMode));
  };

  const handlePreviousTime = () => {
    dispatch(scheduleActions.goPreviousTime());
  };

  const handleNextTime = () => {
    dispatch(scheduleActions.goNextTime());
  };

  const handleToday = () => {
    dispatch(scheduleActions.goToday());
  };

  const handleApplyFilter = (values: IFilterSchedule | undefined) => {
    const { businessTypes, ...rest } = params;

    setSearchParams({
      ...rest,
      ...(values?.businessTypes && values?.businessTypes?.[DEFAULT_NUMBER_ZERO] !== DEFAULT_VALUE_FILTER
        ? { businessTypes: values?.businessTypes }
        : {}),
    });

    setValueFilter({
      ...rest,
      ...(values?.businessTypes ? { businessTypes: values?.businessTypes } : {}),
      ...(employeeIds && { employeeIds: employeeIds }),
    });
  };

  const handleSearchEmployeeChange = (searchKey: string) => {
    setSearchKeyEmployee(searchKey);
  };

  const handleFilterEmployeeChange = (value: any) => {
    setEmployeeIds(value);
  };

  const handleShowAddJob = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createJob);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createJob);
        break;
    }
  };

  const handleShowAddEstimate = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createEstimate, { state: PageTypeEnum.SCHEDULE });
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createEstimate, { state: PageTypeEnum.SCHEDULE });
        break;
    }
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('schedule_title_schedule')}>
    <Box component={'div'} className={cx('container')} id='schedulePage'>
      <BaseToolbar
        // title={t('schedule_title_schedule')}
        // textAction={t('schedule_button_add_schedule')}
        // onAction={handleShowFormSchedule}
      >
        <BaseFilters<IFilterSchedule>
          defaultValue={{ businessTypes: [DEFAULT_VALUE_FILTER], employeeIds: [] }}
          value={{
            businessTypes: [params?.businessTypes || DEFAULT_VALUE_FILTER],
            employeeIds: employeeIds || [],
          }}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Stack gap={1}>
                <Box className={cx('filterItem')}>
                  <InputLabel className={cx('label')}>{t('schedule_label_filter_type')}</InputLabel>

                  <BaseSelect
                    layout='horizontal'
                    // label={t('schedule_label_filter_type')}
                    width={'270px'}
                    options={mockScheduleFilterData}
                    value={value?.businessTypes?.[DEFAULT_NUMBER_ZERO] || EMPTY_STRING}
                    onChange={({ value }) => {
                      onChange?.({ name: 'businessTypes', value: [value] });
                    }}
                  />
                </Box>

                <Box className={cx('filterItem')}>
                  <InputLabel className={cx('label')}>{t('schedule_label_filter_employees')}</InputLabel>

                  <BaseSelect
                    layout='horizontal'
                    // label={t('schedule_label_filter_employees')}
                    multiple
                    isLoading={isLoadingEmployee}
                    width={'270px'}
                    options={employeeList || []}
                    onSearchChange={handleSearchEmployeeChange}
                    value={value?.employeeIds}
                    onChange={handleFilterEmployeeChange}
                  />
                </Box>
              </Stack>
            </Stack>
          )}
        </BaseFilters>

        <BaseSplitButton label={t('schedule_button_add_event')} onClick={handleShowFormSchedule}>
          <Button variant='text' fullWidth onClick={handleShowAddJob}>
            {t('schedule_button_add_job')}
          </Button>

          <Button variant='text' fullWidth onClick={handleShowAddEstimate}>
            {t('schedule_button_add_estimate')}
          </Button>
        </BaseSplitButton>
      </BaseToolbar>

      <HeaderCalendar
        views={views}
        timeValue={timeValue}
        scheduleMode={scheduleMode}
        calendarViewMode={calendarViewMode}
        onPreviousTime={handlePreviousTime}
        onNextTime={handleNextTime}
        onToday={handleToday}
        onChangeCalendarView={handleChangeCalendarView}
      />

      <Box className={cx('calendar')}>
        <Calendar
          view={calendarViewMode}
          date={currentTime}
          events={memoEvents}
          timeLineEvents={timeLineEvents}
          onSelectEvent={onSelectEvent}
          scheduleMode={scheduleMode}
        />
      </Box>

      {isShowFormScheduleModal && (
        <FormScheduleModal onClose={handleCloseFormScheduleModal} scheduleId={scheduleId} />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />

      {isLoading && <Loading />}
    </Box>
    </PageShell>
  );
};

export default Schedule;
